import { Flex, Box, Heading, Text, useToast } from '@chakra-ui/react';
import { MatrixHighlightHelper } from '@texas/components/shared/matrix/MatrixHighlightHelper';
import { CompositionMaterialGrid } from './CompositionMaterialGrid';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import {
  AxisType,
  compositionGroupMatrixApi,
  MatrixAxis,
} from '@texas/api/endpoints/compositionGroup/compositionGroupMatrixApi';
import { useCallback, useEffect, useState } from 'react';
import { CompositionGroup } from '@texas/api/endpoints/compositionGroup/compositionGroupApi';
import { compositionEvents } from '@texas/components/shared/composition/events';
import { useTranslation } from 'react-i18next';
import { useSignalSubscriptionEvents } from '@texas/components/shared/hooks/useSignalSubscriptionEvents';
import { ensureEnumNumber } from '@texas/utils/helpers/enumHelpers';
import { SpinnerWithLabel } from '@texas/components/shared/SpinnerWithLabel';
import { compositionGroupEvents } from '@texas/components/shared/compositionGroup/events';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { request } from '@texas/utils/helpers/httpHelpers';

export function MatrixColors({
  compositionGroupId,
  onLoad,
  colorAxis,
}: {
  compositionGroupId: number;
  onLoad?: (groups: CompositionGroup[]) => void;
  colorAxis: MatrixAxis;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const { data, refetch, loading } = useApiResource(
    compositionGroupMatrixApi.getAxisCells,
  );

  useSignalSubscriptionEvents({
    triggerOnEvent: () => {
      refetch(compositionGroupId, AxisType.Colors);
    },
    signalEvents: [
      compositionEvents.onMaterialAdded,
      compositionEvents.onMaterialChanged,
      compositionEvents.onMaterialRemoved,
      compositionEvents.onAdd,
      compositionEvents.onRestored,
      compositionEvents.onArchived,
      compositionEvents.onRename,
      compositionGroupEvents.onMarkReady,
      compositionGroupEvents.onMarkNotReady,
      compositionEvents.onCopyColorsToAll,
    ],
  });

  useEffect(() => {
    refetch(compositionGroupId, AxisType.Colors);
  }, [compositionGroupId, refetch]);

  useEffect(() => {
    if (!data) return;
    onLoad?.(data);
  }, [data, onLoad]);

  const { request: copyRequest, loading: copyLoading } = useApiRequest(
    compositionGroupMatrixApi.setMatrixCellsColors,
  );

  const [labelWidth, setLabelWidth] = useState(0);
  const onLabelWidth = useCallback(
    (w: number) => setLabelWidth((prev) => Math.max(prev, w)),
    [],
  );
  const performCopy = useCallback(
    async (id: number, index: number) => {
      await request(
        copyRequest,
        [id, index],
        () => {
          toast({
            title: t('general.copied'),
            status: 'success',
            isClosable: true,
          });
          compositionEvents.onCopyColorsToAll.dispatch();
        },
        () => {
          toast({
            title: t('general.actionFailed'),
            status: 'error',
            isClosable: true,
          });
        },
      );
    },
    [copyRequest, toast, t],
  );

  return (
    <Flex flexDir="column" gap={4}>
      <Flex gap={4} align="center">
        {ensureEnumNumber(MatrixAxis, colorAxis) === MatrixAxis.Rows ? (
          <>
            <MatrixHighlightHelper zone="rows" />
            <Box>
              <Heading fontSize="lg">{t('matrix.rows')}</Heading>
              <Text>{t('matrix.colorDesc')}</Text>
            </Box>
          </>
        ) : (
          <>
            <MatrixHighlightHelper zone="columns" />
            <Box>
              <Heading fontSize="lg">{t('matrix.columns')}</Heading>
              <Text>{t('matrix.colorDesc')}</Text>
            </Box>
          </>
        )}
      </Flex>
      {loading && <SpinnerWithLabel label={t('colors.loading')} />}
      {data?.map((y) => {
        return (
          <CompositionMaterialGrid
            key={y.id}
            group={y}
            refetch={() => refetch(compositionGroupId, AxisType.Colors)}
            onCopy={async (id, index) => await performCopy(id, index)}
            loading={copyLoading || loading}
            labelWidth={labelWidth || undefined}
            onLabelWidth={onLabelWidth}
          />
        );
      })}
    </Flex>
  );
}
